/**
 * useMockQuery — dev-only helper that simulates a react-query shape against
 * hardcoded mock data, with URL-query-param state forcing (`?state=loading|
 * empty|error`) so every page can exercise all four branches without a real
 * data layer (which lands in Story 2.2).
 *
 * Spec: docs/architecture/states-pattern.md — Story 2.1 integration scope.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ApiErrorException } from "./errors";

export type MockQueryForcedState = "loading" | "empty" | "error" | undefined;

export interface MockQueryResult<T> {
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  data: T | undefined;
  error: unknown;
  refetch: () => void;
  forcedState: MockQueryForcedState;
}

interface MockQueryOptions {
  /** Optional synthetic latency for the "success" branch. Default 0ms. */
  delayMs?: number;
  /** Emptiness predicate — defaults to `Array.isArray(d) ? d.length === 0 : d == null`. */
  isEmpty?: (data: unknown) => boolean;
}

function defaultIsEmpty(data: unknown): boolean {
  if (data == null) return true;
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

function parseForcedState(search: string): MockQueryForcedState {
  try {
    const params = new URLSearchParams(search);
    const value = params.get("state");
    if (value === "loading" || value === "empty" || value === "error") {
      return value;
    }
  } catch {
    // ignore malformed search strings
  }
  return undefined;
}

/**
 * Build a synthetic query result. Reads `?state=` from the current location
 * to force any of loading/empty/error, otherwise resolves to the supplied
 * success data after `delayMs`.
 *
 * `emptyData` is used when forced to empty (defaults to `[] as unknown as T`).
 */
export function useMockQuery<T>(
  data: T,
  options: MockQueryOptions = {},
): MockQueryResult<T> {
  const location = useLocation();
  const forcedState = useMemo(
    () => parseForcedState(location.search),
    [location.search],
  );

  const { delayMs = 0 } = options;

  const [resolved, setResolved] = useState<boolean>(() => delayMs <= 0);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (forcedState === "loading") {
      setResolved(false);
      return;
    }
    if (delayMs <= 0) {
      setResolved(true);
      return;
    }
    setResolved(false);
    const id = window.setTimeout(() => setResolved(true), delayMs);
    return () => window.clearTimeout(id);
  }, [forcedState, delayMs, nonce]);

  const refetch = () => setNonce((n) => n + 1);

  if (forcedState === "loading") {
    return {
      isPending: true,
      isError: false,
      isSuccess: false,
      data: undefined,
      error: null,
      refetch,
      forcedState,
    };
  }

  if (forcedState === "error") {
    return {
      isPending: false,
      isError: true,
      isSuccess: false,
      data: undefined,
      error: new ApiErrorException({
        kind: "server",
        status: 500,
        message: "Mock error (forced via ?state=error)",
      }),
      refetch,
      forcedState,
    };
  }

  if (forcedState === "empty") {
    const empty = (Array.isArray(data) ? [] : null) as T;
    return {
      isPending: false,
      isError: false,
      isSuccess: true,
      data: empty,
      error: null,
      refetch,
      forcedState,
    };
  }

  if (!resolved) {
    return {
      isPending: true,
      isError: false,
      isSuccess: false,
      data: undefined,
      error: null,
      refetch,
      forcedState,
    };
  }

  return {
    isPending: false,
    isError: false,
    isSuccess: true,
    data,
    error: null,
    refetch,
    forcedState: undefined,
  };
}

export { defaultIsEmpty as isMockQueryEmpty };
